import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Calendar, MapPin, Globe, Mic2, ArrowRight, ExternalLink, Building2, X, CheckCircle, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const upcomingConferences = [
  {
    id: 'hlpf-2026',
    title: 'High-Level Political Forum on Sustainable Development (HLPF 2026)',
    organiser: 'United Nations · ECOSOC',
    date: 'July 2026',
    location: 'UN Headquarters, New York · USA',
    status: 'Upcoming',
    color: '#003399',
    summary: 'Annual UN review of progress on the Sustainable Development Goals. OPAD will deliver written and oral statements on poverty alleviation, gender equity, and climate-resilient livelihoods.',
    themes: ['SDG Review', 'Poverty Alleviation', 'Gender Equity', 'Climate Resilience'],
  },
  {
    id: 'cop31',
    title: 'COP31 — UN Climate Change Conference',
    organiser: 'UNFCCC',
    date: 'November 2026',
    location: 'Host TBD',
    status: 'Upcoming',
    color: '#4A7C59',
    summary: 'OPAD will participate in negotiations and side-events on climate finance, adaptation, and just-transition pathways for vulnerable communities in East Africa and the Sahel.',
    themes: ['Climate Adaptation', 'Loss & Damage', 'Climate Finance', 'Just Transition'],
  },
  {
    id: 'whs-followup',
    title: 'World Humanitarian Summit Follow-up',
    organiser: 'OCHA · UN System',
    date: 'September 2026',
    location: 'Geneva · Switzerland',
    status: 'Upcoming',
    color: '#E08B7A',
    summary: 'Multi-stakeholder review of WHS commitments, with emphasis on localisation, accountability to affected people, and humanitarian-development-peace coherence.',
    themes: ['Localisation', 'Humanitarian Aid', 'Refugee Protection', 'HDP Nexus'],
  },
  {
    id: 'unhcr-77',
    title: '77th Session of the Executive Committee of UNHCR',
    organiser: 'UNHCR',
    date: 'October 2026',
    location: 'Geneva · Switzerland',
    status: 'Upcoming',
    color: '#7BC4E2',
    summary: 'Annual plenary of UNHCR Executive Committee. OPAD will coordinate the NGO statement on climate-displaced populations and durable solutions for protracted situations.',
    themes: ['Refugee Protection', 'Climate Displacement', 'Durable Solutions', 'NGO Coordination'],
  },
  {
    id: 'cerv-buarcos',
    title: 'EU CERV — EcoActive Citizens for a Sustainable Europe',
    organiser: 'European Commission · CERV Programme',
    date: '25–28 February 2026',
    location: 'Buarcos (Figueira da Foz) · Portugal',
    status: 'Upcoming',
    color: '#003399',
    summary: 'First phase of the EU-funded Network of Towns project. OPAD joins partners across Europe for sustainable coastal & marine ecosystem protection, blue literacy, and a Brotherhood Ceremony.',
    themes: ['Civic Engagement', 'Blue Literacy', 'Sustainability', 'Network of Towns'],
  },
  {
    id: 'cerv-santiago',
    title: 'EU CERV — Circular Economy Phase',
    organiser: 'European Commission · CERV Programme',
    date: '1–4 March 2026',
    location: 'Santiago de Compostela · Spain',
    status: 'Upcoming',
    color: '#7BC4E2',
    summary: 'Second phase of the CERV project. Policy dialogue with the Parliament of Galicia, women in the green transition, and circular-economy best practices.',
    themes: ['Circular Economy', 'Green Transition', 'Women & Sustainability', 'Policy Dialogue'],
  },
  {
    id: 'east-africa-conf',
    title: 'OPAD Regional Conference on Inclusive Development',
    organiser: 'OPAD East Africa',
    date: 'Q4 2026',
    location: 'Addis Ababa · Ethiopia',
    status: 'Upcoming',
    color: '#E5B75F',
    summary: 'Strengthening policy dialogue and crisis-response strategies across the Horn of Africa, with stakeholder forums and emergency-preparedness sessions.',
    themes: ['Inclusive Development', 'Crisis Response', 'Stakeholder Dialogue', 'EU Civil Protection'],
  },
  {
    id: 'youth-forum-lagos',
    title: 'Youth Empowerment Forum — West Africa',
    organiser: 'OPAD West Africa',
    date: '20–22 April 2026',
    location: 'Lagos · Nigeria',
    status: 'Upcoming',
    color: '#9DB9A3',
    summary: 'Regional forum on youth employment, digital skills, and entrepreneurship — with sessions co-led by partners and government agencies.',
    themes: ['Youth Employment', 'Digital Skills', 'Entrepreneurship', 'Civic Leadership'],
  },
];

export default function ConferencesPage() {
  const [activeRsvp, setActiveRsvp] = useState(null); // selected conference object
  const [form, setForm] = useState({ name: '', email: '', organisation: '', role: 'Partner', message: '' });
  const [status, setStatus] = useState({ state: 'idle', error: '' });

  const openRsvp = (conf) => {
    setActiveRsvp(conf);
    setForm({ name: '', email: '', organisation: '', role: 'Partner', message: '' });
    setStatus({ state: 'idle', error: '' });
  };
  const closeRsvp = () => setActiveRsvp(null);

  const submitRsvp = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setStatus({ state: 'submitting', error: '' });
    try {
      await axios.post(`${API}/conferences/rsvp`, {
        name: form.name.trim(),
        email: form.email.trim(),
        organisation: form.organisation.trim() || null,
        role: form.role,
        conference_id: activeRsvp.id,
        conference_title: activeRsvp.title,
        message: form.message.trim() || null,
      });
      setStatus({ state: 'success', error: '' });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      setStatus({ state: 'error', error: msg });
    }
  };

  return (
    <div className="min-h-screen bg-[#FDF8F4] pt-20" data-testid="conferences-page">
      {/* Hero */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#003399] via-[#1B4DA0] to-[#3A6DC0]">
          <div className="absolute top-0 right-0 w-[500px] h-[450px] bg-white/10 rounded-bl-[250px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[350px] bg-[#E5B75F]/20 rounded-tr-[180px]" />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <Mic2 className="w-5 h-5 text-[#E5B75F]" />
              <span className="text-white text-sm font-medium">OPAD Speaks Globally</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Manrope'] text-white mb-6">
              Upcoming <span className="text-[#E5B75F]">Conferences</span>
            </h1>
            <p className="text-lg md:text-xl text-white/85 max-w-3xl mx-auto">
              Where OPAD shows up, speaks up, and shapes the global development agenda — UN, EU, regional and partner-led conferences.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quick stats */}
      <section className="py-10 bg-white border-b border-slate-100">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center max-w-4xl mx-auto">
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-[#003399]">{upcomingConferences.length}</div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">Upcoming</p>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-[#4A7C59]">3</div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">UN Forums</p>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-[#E08B7A]">2</div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">EU Programmes</p>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-extrabold text-[#E5B75F]">3</div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">Regional</p>
            </div>
          </div>
        </div>
      </section>

      {/* Conferences list */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {upcomingConferences.map((c, i) => (
              <motion.article
                key={c.id}
                id={c.id}
                className="bg-white rounded-3xl shadow-md border border-slate-100 overflow-hidden flex flex-col"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                data-testid={`conference-card-${c.id}`}
              >
                <div className="h-2" style={{ backgroundColor: c.color }} />
                <div className="p-6 md:p-7 flex-1 flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <span
                      className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
                      style={{ backgroundColor: `${c.color}15`, color: c.color }}
                    >
                      {c.status}
                    </span>
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <Building2 className="w-3.5 h-3.5" />
                      {c.organiser}
                    </span>
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold font-['Manrope'] text-slate-900 leading-snug mb-3">
                    {c.title}
                  </h2>
                  <div className="space-y-1.5 text-sm text-slate-600 mb-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 flex-shrink-0" style={{ color: c.color }} />
                      <span>{c.date}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 flex-shrink-0" style={{ color: c.color }} />
                      <span>{c.location}</span>
                    </div>
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">{c.summary}</p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {c.themes.map((t) => (
                      <span
                        key={t}
                        className="text-[11px] bg-slate-100 text-slate-700 px-2.5 py-1 rounded-full"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => openRsvp(c)}
                    data-testid={`rsvp-open-${c.id}`}
                    className="mt-auto inline-flex items-center justify-center gap-2 text-sm font-semibold text-white rounded-full px-5 py-2.5 transition hover:opacity-90"
                    style={{ backgroundColor: c.color }}
                  >
                    RSVP / Express Interest
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gradient-to-br from-[#003399] to-[#1B4DA0] text-white">
        <div className="container-custom text-center max-w-3xl mx-auto">
          <Globe className="w-12 h-12 mx-auto mb-5 text-[#E5B75F]" />
          <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] mb-4">
            Want to attend or co-organise with OPAD?
          </h2>
          <p className="text-white/85 mb-8">
            We welcome partners, researchers, civil-society networks, and donors to join us at upcoming conferences and side-events.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#E5B75F] text-slate-900 hover:bg-[#D4A64E] rounded-full px-8 py-6 font-semibold">
                Contact the Policy Team <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/events">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#003399] rounded-full px-8 py-6 font-semibold">
                <ExternalLink className="w-4 h-4 mr-2" /> See All Events & Meetings
              </Button>
            </Link>
          </div>
        </div>
      </section>
      {/* RSVP Modal */}
      {activeRsvp && (
        <div
          className="fixed inset-0 z-[100] flex items-end md:items-center justify-center bg-slate-900/60 backdrop-blur-sm p-0 md:p-4"
          onClick={closeRsvp}
          data-testid="rsvp-modal"
        >
          <motion.div
            className="bg-white w-full md:max-w-lg rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden"
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-2" style={{ backgroundColor: activeRsvp.color }} />
            <div className="p-6 md:p-7">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: activeRsvp.color }}>
                    {activeRsvp.organiser}
                  </span>
                  <h3 className="text-xl font-bold font-['Manrope'] text-slate-900 leading-snug">
                    RSVP — {activeRsvp.title}
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">{activeRsvp.date} · {activeRsvp.location}</p>
                </div>
                <button
                  type="button"
                  onClick={closeRsvp}
                  className="text-slate-400 hover:text-slate-700"
                  aria-label="Close"
                  data-testid="rsvp-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {status.state === 'success' ? (
                <div className="text-center py-6" data-testid="rsvp-success">
                  <div className="w-14 h-14 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="w-7 h-7" />
                  </div>
                  <h4 className="font-bold text-slate-900 mb-1">Thank you!</h4>
                  <p className="text-sm text-slate-600 mb-5">We've received your interest and emailed you a confirmation. The OPAD team will be in touch with details.</p>
                  <Button onClick={closeRsvp} className="rounded-full px-6">Close</Button>
                </div>
              ) : (
                <form onSubmit={submitRsvp} className="space-y-3" data-testid="rsvp-form">
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      required
                      type="text"
                      placeholder="Full name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#003399]/30 focus:border-[#003399] outline-none"
                      data-testid="rsvp-name"
                    />
                    <input
                      required
                      type="email"
                      placeholder="Email address"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#003399]/30 focus:border-[#003399] outline-none"
                      data-testid="rsvp-email"
                    />
                  </div>
                  <input
                    type="text"
                    placeholder="Organisation (optional)"
                    value={form.organisation}
                    onChange={(e) => setForm({ ...form, organisation: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#003399]/30 focus:border-[#003399] outline-none"
                    data-testid="rsvp-organisation"
                  />
                  <select
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#003399]/30 focus:border-[#003399] outline-none bg-white"
                    data-testid="rsvp-role"
                  >
                    <option value="Donor">Donor</option>
                    <option value="Partner">Partner / NGO</option>
                    <option value="Press">Press / Media</option>
                    <option value="Academic">Academic / Researcher</option>
                    <option value="Government">Government / Diplomatic</option>
                    <option value="Civil Society">Civil Society</option>
                    <option value="Other">Other</option>
                  </select>
                  <textarea
                    rows="3"
                    placeholder="Message (optional) — questions, context, side-event interest..."
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#003399]/30 focus:border-[#003399] outline-none resize-none"
                    data-testid="rsvp-message"
                  />

                  {status.state === 'error' && (
                    <div className="flex items-start gap-2 bg-red-50 text-red-700 text-xs p-3 rounded-lg" data-testid="rsvp-error">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>{status.error}</span>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={status.state === 'submitting'}
                    data-testid="rsvp-submit"
                    className="w-full rounded-full text-white py-5 font-semibold disabled:opacity-60"
                    style={{ backgroundColor: activeRsvp.color }}
                  >
                    {status.state === 'submitting' ? 'Sending...' : 'Send Interest'}
                  </Button>
                  <p className="text-[11px] text-slate-500 text-center">
                    By submitting you consent to OPAD contacting you about this conference. Disposable email domains are blocked.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

// Export the data so the Navbar dropdown can reuse it.
export { upcomingConferences };
